import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type { FriendsResponse } from '../lib/types';
import { Button, EmptyState, ErrorState, Panel } from '../components/ui';
import { formatNumber, timeAgo } from '../lib/format';

export function FriendsPage() {
  const { push } = useToast();
  const state = useAsync<FriendsResponse>(() => api.get('/friends'));
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  async function sendRequest() {
    if (!username.trim()) return;
    setBusy('request');
    try {
      const result = await api.post<{ status: string; username: string }>('/friends/request', {
        username: username.trim(),
      });
      push(
        'success',
        result.status === 'ACCEPTED'
          ? `You and ${result.username} are now friends!`
          : `Request sent to ${result.username}.`,
        '♥',
      );
      setUsername('');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not send that request.');
    } finally {
      setBusy(null);
    }
  }

  async function respond(requestId: string, accept: boolean) {
    setBusy(requestId);
    try {
      await api.post('/friends/respond', { requestId, accept });
      push(accept ? 'success' : 'info', accept ? 'You have a new friend!' : 'Request declined.');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(userId: string, name: string) {
    setBusy(userId);
    try {
      await api.post('/friends/remove', { userId });
      push('info', `Removed ${name} from your friends.`);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const data = state.data;
  const bonusPercent = data ? Math.round((data.limits.feedBonus - 1) * 100) : 25;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Friends</h1>
          <div className="page-head__sub">
            Feeding a friend's Pokémon is worth {bonusPercent}% more EXP.
          </div>
        </div>
      </div>

      <Panel title="♥ Add a friend">
        <div className="row">
          <input
            className="input"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void sendRequest()}
            placeholder="Their username"
            maxLength={20}
          />
          <Button
            variant="primary"
            disabled={!username.trim() || busy === 'request'}
            loading={busy === 'request'}
            onClick={() => void sendRequest()}
          >
            Send request
          </Button>
        </div>
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)', marginBottom: 0 }}>
          Friendship is mutual and needs their agreement. Following someone is one-way and doesn't.
        </p>
      </Panel>

      {data && data.incoming.length > 0 && (
        <Panel title="◆ Requests for you" hint={`${data.incoming.length} waiting`}>
          <div className="stack-sm">
            {data.incoming.map((request) => (
              <div key={request.id} className="pxwell row row--between row--wrap" style={{ padding: 'var(--gap-sm)' }}>
                <div>
                  <div className="mono tiny">{request.trainerName}</div>
                  <div className="tiny muted">
                    @{request.username} · Level {request.level} · {timeAgo(request.createdAt)}
                  </div>
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <Button
                    size="sm"
                    variant="go"
                    disabled={busy === request.id}
                    onClick={() => void respond(request.id, true)}
                  >
                    Accept
                  </Button>
                  <Button size="sm" disabled={busy === request.id} onClick={() => void respond(request.id, false)}>
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel
        title="♥ Your friends"
        hint={data ? `${data.friends.length} / ${data.limits.max}` : ''}
      >
        {state.loading ? (
          <div className="pxwell skeleton" style={{ height: 160 }} />
        ) : (data?.friends.length ?? 0) === 0 ? (
          <EmptyState
            art="♡"
            title="No friends yet"
            action={
              <Link className="btn btn--primary" to="/community">
                Find trainers
              </Link>
            }
          >
            Visit a few Groves — most trainers add back the people who help them.
          </EmptyState>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {data!.friends.map((friend) => (
              <div key={friend.userId} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                <div className="stack-sm">
                  <div className="row row--between">
                    <span className="mono tiny">{friend.trainerName}</span>
                    <span className="chip">Lv {friend.level}</span>
                  </div>
                  <div className="tiny muted">@{friend.username} · {friend.title}</div>
                  <div className="row row--wrap tiny muted" style={{ gap: 'var(--gap-sm)' }}>
                    <span>🌱 {friend.pokemonOut} out</span>
                    {friend.shinyCount > 0 && (
                      <span style={{ color: 'var(--plum-dark)' }}>✦ {formatNumber(friend.shinyCount)}</span>
                    )}
                    <span>{timeAgo(friend.lastSeenAt)}</span>
                  </div>
                  <div className="row" style={{ gap: 4 }}>
                    <Link className="btn btn--sm btn--go" to={`/grove/${friend.username}`} style={{ flex: 1 }}>
                      Visit Grove
                    </Link>
                    <Link className="btn btn--sm" to={`/trades?with=${friend.username}`}>
                      ⇄
                    </Link>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === friend.userId}
                      onClick={() => void remove(friend.userId, friend.trainerName)}
                      title="Remove friend"
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {data && data.outgoing.length > 0 && (
        <Panel title="◷ Sent, awaiting a reply" raised={false}>
          <div className="stack-sm">
            {data.outgoing.map((request) => (
              <div key={request.id} className="row row--between tiny">
                <span>@{request.username}</span>
                <span className="muted">{timeAgo(request.createdAt)}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
